import { toast } from "react-toastify";
import { customToast, formatDate } from "../utility";
import {
  EMPTY_STRING,
  inventoryConstants,
  toastMessages,
  toastType,
} from "../../constants";

const beautifyDateForExport = (date) => {
  if (!date) {
    return EMPTY_STRING;
  }
  return formatDate(date, false, true);
};

const beautifyStringForImport = (value) => {
  if (value == null) return "";

  let str = String(value);

  // Normalize problematic characters
  str = str
    .replace(/\r?\n|\r/g, " ") // remove new lines
    .replace(/\t/g, " ") // remove tabs
    .replace(/\s+/g, " ") // collapse multiple spaces
    .trim();

  // Escape double quotes (CSV standard)
  str = str.replace(/"/g, '""');

  // Always wrap in quotes to be safe
  return `"${str}"`;
};
// Wrap CSV cell values that contain commas, quotes, or line breaks so they stay in one column.
function escapeCsvValue(value) {
  if (value === null || value === undefined) {
    return "";
  }
  const stringValue = String(value);
  if (/[",\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

function convertArrayOfObjectsToCSV(array) {
  if (array.length === 0) {
    return "";
  }

  let result;

  const columnDelimiter = ",";
  const lineDelimiter = "\n";
  const keys = Object.keys(array[0]);

  result = "";
  result += keys.map(escapeCsvValue).join(columnDelimiter);
  result += lineDelimiter;

  array.forEach((item) => {
    let ctr = 0;
    keys.forEach((key) => {
      if (ctr > 0) result += columnDelimiter;

      result += escapeCsvValue(item[key]);

      ctr++;
    });
    result += lineDelimiter;
  });
  return result;
}

function downloadCSV(
  array,
  fileName,
  date = `${new Date().toISOString().slice(8, 10)}-${new Date()
    .toISOString()
    .slice(5, 7)}-${new Date().toISOString().slice(0, 4)}`
) {
  if (array.length === 0) {
    customToast(toastMessages.CANNOT_BE_EXPORT_ARRAY_EMPTY, toastType.ERROR);
    return;
  }
  const link = document.createElement("a");
  let csv = convertArrayOfObjectsToCSV(array);
  if (csv == null) return;

  const filename = `${fileName ? fileName : "export"}-${date}.csv`;

  if (!csv.match(/^data:text\/csv/i)) {
    csv = `data:text/csv;charset=utf-8,${encodeURI(csv).replaceAll(
      "#",
      "%23"
    )}`;
  }

  link.setAttribute("href", csv);
  link.setAttribute("download", filename);
  link.click();
}

const exportTagFunc = (tags) => {
  if (!tags) {
    return "";
  }
  return tags.join("; ").replaceAll("#", EMPTY_STRING);
};

const importTagFunc = (tags) => {
  // clean custom tag
  const cleanTagsFunc = (tags) => {
    let customTagRegex = /^#?custom$/i;
    let isCustomTag = tags.some((tag) => customTagRegex.test(tag));

    // remove all custom tags (case insensitive)
    let filteredTags = tags.filter((tag) => {
      return !customTagRegex.test(tag);
    });

    if (isCustomTag) {
      filteredTags.unshift(inventoryConstants.CUSTOM_ITEM_TAG);
    }

    return filteredTags;
  };

  if (!tags) {
    return [];
  }
  // convert tag string to array
  let tagsArray = tags.replace(/(?<=^|; )\b(?=\S)/g, "#").split("; ");
  let cleanTagsArray = cleanTagsFunc(tagsArray);
  return cleanTagsArray;
};

const importUpcFunc = (value) => {
  return String(value);
};

const exportUpcFunc = (value) => {
  return String(value);
};

const exportStringArrayFunc = (array) => {
  return array.join("; ");
};

const importStringArrayFunc = (array) => {
  return array.split("; ");
};

const createMappedArray = (csvData, predefinedColumns, columnMapping) => {
  return csvData.map((row) => {
    const mappedRow = {};
    predefinedColumns.forEach((predefinedColumn) => {
      const csvColumn = columnMapping[predefinedColumn];
      if (csvColumn && row.hasOwnProperty(csvColumn)) {
        mappedRow[predefinedColumn] = row[csvColumn];
      }
    });
    return mappedRow;
  });
};

const handleColumnMapping = (
  predefinedColumn,
  selectedCsvColumn,
  setColumnMapping
) => {
  setColumnMapping((prevMapping) => ({
    ...prevMapping,
    [predefinedColumn]: selectedCsvColumn,
  }));
};

const handleFileLoaded = (
  data,
  fileInfo,
  predefinedColumns,
  setCsvData,
  setColumnMapping
) => {
  // handle empty file
  if (!data.length) {
    customToast(toastMessages.NO_RECORD_FOUND, toastType.ERROR);
    return;
  }
  if (fileInfo.type === "text/csv") {
    const defaultMapping = {};
    const csvColumns = Object.keys(data[0]);

    csvColumns.forEach((csvColumn) => {
      if (predefinedColumns.includes(csvColumn)) {
        defaultMapping[csvColumn] = csvColumn;
      }
    });
    setCsvData(data);
    setColumnMapping(defaultMapping);
  } else {
    customToast(toastMessages.ONLY_CSV_ALLOWED, toastType.ERROR);
  }
};

const validateDateForExport = (value) => {
  if (value == null || value === EMPTY_STRING) {
    return true;
  }

  const normalizedValue = String(value).trim();
  const dateRegex = /^(\d{2}[-/]\d{2}[-/]\d{4}|\d{8})$/;
  return dateRegex.test(normalizedValue);
};

/**
 * Async export function that handles data fetching with toast notifications
 * Toast shows immediately while data is being fetched and updates with a single ID
 * @param {Function} fetchDataFn - Async function that returns the data to export
 * @param {Function} transformDataFn - Function that transforms fetched data to export format
 * @param {String} fileName - Name of the exported file (without extension)
 */
const handleExportWithToastAsync = async (
  fetchDataFn,
  transformDataFn,
  fileName
) => {
  try {
    // Show initial toast IMMEDIATELY before fetching data and capture the toast ID
    const toastId = customToast(toastMessages.PREPARING_FILE, toastType.INFO);
    // Fetch the data and transform it for csv file columns
    const data = await fetchDataFn();
    const exportData = transformDataFn(data);

    // Update the same toast after a delay
    setTimeout(() => {
      toast.update(toastId, {
        render: toastMessages.DOWNLOAD_START_IN_FEW_SECONDS,
        type: toastType.SUCCESS,
      });
      // Download the CSV after showing the updated message
      downloadCSV(exportData, fileName);
    }, 1000);
  } catch (error) {
    console.error("Export error:", error);
    customToast(toastMessages.SOMETHING_WENT_WRONG, toastType.ERROR);
  }
};

export {
  downloadCSV,
  exportTagFunc,
  importTagFunc,
  exportUpcFunc,
  importUpcFunc,
  handleFileLoaded,
  createMappedArray,
  handleColumnMapping,
  beautifyDateForExport,
  validateDateForExport,
  exportStringArrayFunc,
  importStringArrayFunc,
  beautifyStringForImport,
  handleExportWithToastAsync,
};
